import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { GameCover } from '@/components/ui/GameCover'
import { Icon } from '@/components/ui/Icon'
import type { DismissReason, DismissedRecommendationEntry } from '@/types'
import { DISMISS_REASONS, REASON_BY_ID } from './DismissButton'

type FilterKey = 'all' | DismissReason | 'no-reason'

interface DismissedRecommendationsModalProps {
  entries: DismissedRecommendationEntry[]
  groupName: string
  onClose: () => void
  onRestore: (appId: string) => void
  onSetReason: (appId: string, reason: DismissReason) => void
  canEdit?: boolean
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.round(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  return `${d}d ago`
}

export function DismissedRecommendationsModal({
  entries,
  groupName,
  onClose,
  onRestore,
  onSetReason,
  canEdit = true,
}: DismissedRecommendationsModalProps) {
  const [filter, setFilter] = useState<FilterKey>('all')

  const counts = useMemo(() => {
    const c: Record<FilterKey, number> = {
      all: entries.length,
      'wrong-vibe': 0,
      'wrong-for-group': 0,
      'low-quality': 0,
      'played-elsewhere': 0,
      'no-reason': 0,
    }
    for (const e of entries) {
      if (e.reason) c[e.reason] += 1
      else c['no-reason'] += 1
    }
    return c
  }, [entries])

  const shown = entries.filter((e) => {
    if (filter === 'all') return true
    if (filter === 'no-reason') return !e.reason
    return e.reason === filter
  })

  return createPortal(
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div className="cg-modal large dismissed" onClick={(e) => e.stopPropagation()}>
        <div className="cg-modal-header">
          <div>
            <h2>Dismissed recommendations</h2>
            <p className="cg-modal-sub">
              {entries.length === 0
                ? `Nothing hidden for ${groupName}.`
                : `${entries.length} hidden from ${groupName}'s recommendations.${canEdit ? ' Restore any to bring them back.' : ''}`}
            </p>
          </div>
          <button className="cg-icon-btn" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        {entries.length > 0 && (
          <div className="cg-dismissed-tabs">
            <button
              type="button"
              className="cg-dismissed-tab"
              data-active={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              All <span className="cg-dismissed-tab-count">{counts.all}</span>
            </button>
            {DISMISS_REASONS.map(
              (r) =>
                counts[r.id] > 0 && (
                  <button
                    key={r.id}
                    type="button"
                    className="cg-dismissed-tab"
                    data-active={filter === r.id}
                    onClick={() => setFilter(r.id)}
                  >
                    {r.label}{' '}
                    <span className="cg-dismissed-tab-count">{counts[r.id]}</span>
                  </button>
                ),
            )}
            {counts['no-reason'] > 0 && (
              <button
                type="button"
                className="cg-dismissed-tab"
                data-active={filter === 'no-reason'}
                onClick={() => setFilter('no-reason')}
              >
                No reason{' '}
                <span className="cg-dismissed-tab-count">{counts['no-reason']}</span>
              </button>
            )}
          </div>
        )}

        <div className="cg-dismissed-list">
          {shown.map((e) => {
            const reason = e.reason ? REASON_BY_ID[e.reason] : null
            return (
              <div key={e.appId} className="cg-dismissed-row">
                <GameCover game={e.game} size="sm" />
                <div className="cg-dismissed-row-body">
                  <div className="cg-title">{e.game.title}</div>
                  <div className="cg-subtitle">{e.game.tags.slice(0, 3).join(' · ')}</div>
                  <div className="cg-dismissed-meta">
                    {reason ? (
                      <span className="cg-dismissed-reason" data-reason={e.reason ?? undefined}>
                        <span className="cg-dismissed-reason-dot" />
                        {reason.label}
                      </span>
                    ) : !canEdit ? (
                      <span className="cg-dismissed-reason" data-reason={undefined}>
                        <span className="cg-dismissed-reason-dot" />
                        No reason
                      </span>
                    ) : (
                      <select
                        className="cg-dismissed-reason-pick"
                        defaultValue=""
                        onChange={(ev) => {
                          const value = ev.target.value
                          if (value) onSetReason(e.appId, value as DismissReason)
                        }}
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        <option value="">Add a reason…</option>
                        {DISMISS_REASONS.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    )}
                    <span className="cg-dismissed-time">Hidden {relativeTime(e.dismissedAt)}</span>
                  </div>
                </div>
                {canEdit && (
                  <div className="cg-dismissed-row-actions">
                    <button
                      type="button"
                      className="cg-btn cg-btn-primary tiny"
                      onClick={() => onRestore(e.appId)}
                    >
                      <Icon name="refresh" size={11} /> Restore
                    </button>
                  </div>
                )}
              </div>
            )
          })}
          {shown.length === 0 && (
            <div className="cg-empty">
              <p>
                {entries.length === 0
                  ? "You haven't hidden anything yet. The × on each recommendation will send it here."
                  : 'No dismissed games match this filter.'}
              </p>
            </div>
          )}
        </div>

        <div className="cg-modal-footer">
          <button type="button" className="cg-btn cg-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
